/**
 * Ethereal Mist Effect
 *
 * 幻想の霧 - サイバーパンク・ネオン霧（パープル系）
 */

import * as THREE from 'three';
import type { SystemObject } from '../types.js';

/**
 * Create ethereal mist layers
 */
export function createEtherealMist(
  scene: THREE.Scene,
  systemsRef: React.MutableRefObject<SystemObject[]>
): void {
  const mistGroup = new THREE.Group() as unknown as SystemObject;
  const layerCount = 4;

  for (let layer = 0; layer < layerCount; layer++) {
    const geometry = new THREE.PlaneGeometry(5000, 2500, 150, 80);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        beat: { value: 0 },
        mouse: { value: new THREE.Vector2() },
        layerIndex: { value: layer },
        layerCount: { value: layerCount }
      },
      vertexShader: `
        varying vec2 vUv;
        varying float vNoise;
        varying float vDepth;
        uniform float time;
        uniform float beat;
        uniform vec2 mouse;
        uniform float layerIndex;
        uniform float layerCount;

        // Simplex noise
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          vec4 x = x_ * ns.x + ns.yyyy;
          vec4 y = y_ * ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        void main() {
          vUv = uv;
          vec3 pos = position;

          float layerOffset = layerIndex * 3.14159;
          float slowTime = time * (0.08 + layerIndex * 0.02);

          // 非常にゆっくりとした霧の動き
          float noise1 = snoise(vec3(pos.x * 0.0008, pos.y * 0.0008, slowTime + layerOffset)) * 120.0;
          float noise2 = snoise(vec3(pos.x * 0.002, pos.y * 0.002, slowTime * 1.3 + layerOffset)) * 60.0;
          float noise3 = snoise(vec3(pos.x * 0.004, pos.y * 0.004, slowTime * 1.7)) * 30.0;

          // 横方向にゆっくり流れる
          float drift = sin(time * 0.1 + layerIndex) * 50.0;
          pos.x += drift;

          // 上下の揺らぎ
          pos.z = noise1 + noise2 + noise3;

          // ビートでふわっと膨らむ
          pos.z += beat * 80.0 * snoise(vec3(pos.xy * 0.003, time));

          // マウスで霧を払う
          vec2 mouseWorld = mouse * vec2(2000.0, 1000.0);
          float mouseDist = length(pos.xy - mouseWorld);
          pos.z += exp(-mouseDist * 0.002) * mouse.x * 100.0;

          vNoise = (noise1 + noise2) / 150.0;
          vDepth = layerIndex / (layerCount - 1.0);

          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        varying float vNoise;
        varying float vDepth;
        uniform float time;
        uniform float beat;
        uniform float layerIndex;
        uniform float layerCount;

        void main() {
          // 🎨 サイバーパンク・ネオン霧（パープル系）
          vec3 voidBlack = vec3(0.02, 0.0, 0.06);     // ほぼ黒（深淵）
          vec3 deepPurple = vec3(0.2, 0.0, 0.35);     // ディープパープル
          vec3 neonViolet = vec3(0.4, 0.1, 0.7);      // ネオンバイオレット
          vec3 neonCyan = vec3(0.0, 0.3, 0.5);        // ネオンシアン（アクセント）

          // レイヤーごとに色を変える（奥ほど暗く）
          float depthFactor = 1.0 - vDepth * 0.4;

          // ノイズに応じた色の変化
          float t = vNoise * 0.5 + 0.5;
          vec3 color = mix(voidBlack, deepPurple, smoothstep(0.2, 0.5, t));
          color = mix(color, neonViolet, smoothstep(0.5, 0.75, t));

          // シアンのアクセント（ランダムに）
          float cyanAccent = sin(vUv.x * 5.0 + time * 0.3) * sin(vUv.y * 3.0 - time * 0.2);
          cyanAccent = smoothstep(0.3, 0.7, cyanAccent) * 0.3;
          color = mix(color, neonCyan, cyanAccent);

          color *= depthFactor;

          // ゆっくりした色の揺らぎ（より強いパープルシフト）
          float colorShift = sin(vUv.x * 3.0 + time * 0.2 + layerIndex) * 0.5 + 0.5;
          color = mix(color, color * vec3(1.1, 0.9, 1.4), colorShift * 0.3);

          // ビートで明るくフラッシュ
          color *= 1.0 + beat * 0.3;

          // 霧のような柔らかい透明度
          float noiseAlpha = smoothstep(-0.3, 0.5, vNoise);

          // エッジをソフトに
          float edgeFade = smoothstep(0.0, 0.15, vUv.x) * smoothstep(1.0, 0.85, vUv.x);
          edgeFade *= smoothstep(0.0, 0.2, vUv.y) * smoothstep(1.0, 0.8, vUv.y);

          // レイヤーごとの基本透明度（少し濃く）
          float baseAlpha = 0.03 + (1.0 - vDepth) * 0.03;
          float alpha = baseAlpha * noiseAlpha * edgeFade;

          // ビートでふわっと
          alpha *= 1.0 + beat * 0.15;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    const mist = new THREE.Mesh(geometry, material);
    mist.position.set(0, -200 + layer * 100, -400 - layer * 250);
    mist.rotation.x = -Math.PI / 4 + layer * 0.05;
    mistGroup.add(mist);
  }

  mistGroup.userData.update = (ctx) => {
    mistGroup.children.forEach((child) => {
      const mesh = child as THREE.Mesh;
      const mat = mesh.material as THREE.ShaderMaterial;
      mat.uniforms.time.value = ctx.time;
      mat.uniforms.beat.value = ctx.beat.intensity;
      mat.uniforms.mouse.value.set(ctx.mouse.x, ctx.mouse.y);
    });
  };

  scene.add(mistGroup);
  systemsRef.current.push(mistGroup);
}
